import type {
  FlowConnection,
  FlowDefinition,
  FlowFieldDefinition,
  FlowNode,
  FlowNodeBinding,
  FlowScope,
  FlowValueType,
  PageFlowState,
  PageFlowStateEntry,
  SelectedVase,
} from '../types';

export const FLOW_UPDATED_AT_FALLBACK = '1970-01-01T00:00:00.000Z';
export const VASE_CUSTOMIZER_FLOW_PRESET_ID = 'vase-customizer';
export const VASE_FLOW_FIELD_IDS = {
  chosenProduct: 'chosenProduct',
  chosenFinish: 'chosenFinish',
  chosenColours: 'chosenColours',
  chosenCombination: 'chosenCombination',
  productName: 'productName',
  productPrice: 'productPrice',
  productMaterial: 'productMaterial',
} as const;

export const FLOW_VALUE_TYPES: FlowValueType[] = [
  'context.selection',
  'context.item',
  'entity.product',
  'entity.request-item',
  'scalar.string',
  'scalar.number',
  'scalar.boolean',
  'list.string',
  'list.request-item',
  'entity.combination',
  'event.trigger',
  'event.click',
  'event.product-select',
];

const FLOW_SCOPES: FlowScope[] = ['page', 'section'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function normalizeString(value: unknown, fallback: string) {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : fallback;
}

function flowValuesEqual(left: unknown, right: unknown) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function normalizeFlowValueType(value: unknown, fallback: FlowValueType): FlowValueType {
  return FLOW_VALUE_TYPES.includes(value as FlowValueType) ? (value as FlowValueType) : fallback;
}

function normalizeFlowScope(value: unknown, fallback: FlowScope): FlowScope {
  return FLOW_SCOPES.includes(value as FlowScope) ? (value as FlowScope) : fallback;
}

function normalizeBinding(value: unknown, fallbackPrefix: string, index: number): FlowNodeBinding | null {
  if (!isRecord(value)) return null;
  const socketId = normalizeString(value.socketId, `${fallbackPrefix}-socket-${index + 1}`);
  const fieldId = normalizeString(value.fieldId, '');
  if (!fieldId) return null;
  return { socketId, fieldId };
}

function normalizeBindings(value: unknown, fallbackPrefix: string): FlowNodeBinding[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry, index) => normalizeBinding(entry, fallbackPrefix, index))
    .filter((entry): entry is FlowNodeBinding => Boolean(entry));
}

function normalizeNode(value: unknown, index: number): FlowNode | null {
  if (!isRecord(value)) return null;
  const id = normalizeString(value.id, `flow-node-${index + 1}`);
  const blockId = normalizeString(value.blockId, '');
  const blockType = normalizeString(value.blockType, '');
  if (!blockId || !blockType) return null;
  return {
    id,
    blockId,
    blockType,
    adapterId: typeof value.adapterId === 'string' && value.adapterId.trim().length > 0 ? value.adapterId.trim() : undefined,
    config: isRecord(value.config) ? { ...value.config } : undefined,
    inputs: normalizeBindings(value.inputs, `${id}-input`),
    outputs: normalizeBindings(value.outputs, `${id}-output`),
  };
}

function normalizeNodes(value: unknown): FlowNode[] {
  if (!Array.isArray(value)) return [];
  return value.map((entry, index) => normalizeNode(entry, index)).filter((entry): entry is FlowNode => Boolean(entry));
}

function normalizeConnection(value: unknown, index: number): FlowConnection | null {
  if (!isRecord(value)) return null;
  const id = normalizeString(value.id, `flow-connection-${index + 1}`);
  const fromNodeId = normalizeString(value.fromNodeId, '');
  const fromSocketId = normalizeString(value.fromSocketId, '');
  const toNodeId = normalizeString(value.toNodeId, '');
  const toSocketId = normalizeString(value.toSocketId, '');
  const fieldId = normalizeString(value.fieldId, '');
  if (!fromNodeId || !fromSocketId || !toNodeId || !toSocketId || !fieldId) return null;
  return {
    id,
    fromNodeId,
    fromSocketId,
    toNodeId,
    toSocketId,
    fieldId,
  };
}

function normalizeConnections(value: unknown): FlowConnection[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry, index) => normalizeConnection(entry, index))
    .filter((entry): entry is FlowConnection => Boolean(entry));
}

function normalizeField(value: unknown, index: number): FlowFieldDefinition | null {
  if (!isRecord(value)) return null;
  const id = normalizeString(value.id, `field-${index + 1}`);
  const label = normalizeString(value.label, id);
  return {
    id,
    label,
    type: normalizeFlowValueType(value.type, 'scalar.string'),
    multiple: value.multiple === true,
  };
}

function createFlowEntityId(prefix: string): string {
  const generatedId =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  return `${prefix}-${generatedId}`;
}

export function getViewerControlActiveProductFieldId(blockId: string) {
  return `viewer-control-active-product-${blockId}`;
}

export function getViewerControlActiveFinishFieldId(blockId: string) {
  return `viewer-control-active-finish-${blockId}`;
}

export function getViewerControlActiveCombinationFieldId(blockId: string) {
  return `viewer-control-active-combination-${blockId}`;
}

export function getViewerControlOpenedFieldId(blockId: string) {
  return `viewer-control-opened-${blockId}`;
}

export function getViewerControlClosedFieldId(blockId: string) {
  return `viewer-control-closed-${blockId}`;
}

export function getViewerControlModelLoadedFieldId(blockId: string) {
  return `viewer-control-model-loaded-${blockId}`;
}

export function buildViewerControlFields(blockId: string): FlowFieldDefinition[] {
  return [
    {
      id: getViewerControlActiveProductFieldId(blockId),
      label: 'Active Viewer Product',
      type: 'entity.product',
    },
    {
      id: getViewerControlActiveFinishFieldId(blockId),
      label: 'Active Viewer Finish',
      type: 'scalar.string',
    },
    {
      id: getViewerControlActiveCombinationFieldId(blockId),
      label: 'Active Viewer Combination',
      type: 'entity.combination',
    },
    {
      id: getViewerControlOpenedFieldId(blockId),
      label: 'Viewer Opened',
      type: 'event.trigger',
    },
    {
      id: getViewerControlClosedFieldId(blockId),
      label: 'Viewer Closed',
      type: 'event.trigger',
    },
    {
      id: getViewerControlModelLoadedFieldId(blockId),
      label: 'Viewer Model Loaded',
      type: 'event.trigger',
    },
  ];
}

function upsertFlowFields(
  existingFields: FlowFieldDefinition[],
  requiredFields: FlowFieldDefinition[]
): FlowFieldDefinition[] {
  const fieldMap = new Map(existingFields.map((field) => [field.id, field]));
  requiredFields.forEach((field) => {
    fieldMap.set(field.id, field);
  });

  const nextFields = [...existingFields];
  requiredFields.forEach((field) => {
    if (!existingFields.some((entry) => entry.id === field.id)) {
      nextFields.push(field);
    }
  });

  return nextFields.map((field) => fieldMap.get(field.id) ?? field);
}

function createDerivedConnectionId(
  fromNodeId: string,
  fromSocketId: string,
  toNodeId: string,
  toSocketId: string,
  fieldId: string
): string {
  return `flow-connection-${fromNodeId}-${fromSocketId}-${toNodeId}-${toSocketId}-${fieldId}`;
}

export function normalizeFlowDefinitions(value: unknown): FlowDefinition[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry, index): FlowDefinition | null => {
      if (!isRecord(entry)) return null;
      const id = normalizeString(entry.id, `flow-${index + 1}`);
      const name = normalizeString(entry.name, `Flow ${index + 1}`);
      return {
        id,
        name,
        scope: normalizeFlowScope(entry.scope, 'page'),
        sectionId: typeof entry.sectionId === 'string' && entry.sectionId.trim().length > 0 ? entry.sectionId.trim() : undefined,
        presetId: typeof entry.presetId === 'string' && entry.presetId.trim().length > 0 ? entry.presetId.trim() : undefined,
        fields: Array.isArray(entry.fields)
          ? entry.fields
              .map((field, fieldIndex) => normalizeField(field, fieldIndex))
              .filter((field): field is FlowFieldDefinition => Boolean(field))
          : [],
        nodes: normalizeNodes(entry.nodes),
        connections: normalizeConnections(entry.connections),
      };
    })
    .filter((entry): entry is FlowDefinition => Boolean(entry));
}

export function rebuildFlowConnections(flow: FlowDefinition): FlowConnection[] {
  const nextConnections: FlowConnection[] = [];
  const outputBindings = flow.nodes.flatMap((node) =>
    node.outputs.map((binding) => ({
      nodeId: node.id,
      socketId: binding.socketId,
      fieldId: binding.fieldId,
    }))
  );

  const seenIds = new Set<string>();
  flow.nodes.forEach((node) => {
    node.inputs.forEach((binding) => {
      outputBindings.forEach((output) => {
        if (output.fieldId !== binding.fieldId) return;
        if (output.nodeId === node.id) return;
        const id = createDerivedConnectionId(
          output.nodeId,
          output.socketId,
          node.id,
          binding.socketId,
          binding.fieldId
        );
        if (seenIds.has(id)) return;
        seenIds.add(id);
        nextConnections.push({
          id,
          fromNodeId: output.nodeId,
          fromSocketId: output.socketId,
          toNodeId: node.id,
          toSocketId: binding.socketId,
          fieldId: binding.fieldId,
        });
      });
    });
  });

  return nextConnections;
}

export function setFlowNodeSocketBinding(
  flow: FlowDefinition,
  nodeId: string,
  direction: 'input' | 'output',
  socketId: string,
  fieldId: string | null
): FlowDefinition {
  const nodes = flow.nodes.map((node) => {
    if (node.id !== nodeId) return node;
    const bindings = direction === 'input' ? node.inputs : node.outputs;
    const nextBindings = bindings.filter((binding) => binding.socketId !== socketId);
    if (fieldId) {
      nextBindings.push({
        socketId,
        fieldId,
      });
    }
    return direction === 'input'
      ? {
          ...node,
          inputs: nextBindings,
        }
      : {
          ...node,
          outputs: nextBindings,
        };
  });

  const nextFlow = {
    ...flow,
    nodes,
  };

  return {
    ...nextFlow,
    connections: rebuildFlowConnections(nextFlow),
  };
}

export function connectFlowSockets(
  flow: FlowDefinition,
  args: {
    outputNodeId: string;
    outputSocketId: string;
    inputNodeId: string;
    inputSocketId: string;
    fieldType: FlowValueType;
    fieldLabel?: string;
  }
): FlowDefinition {
  const {
    outputNodeId,
    outputSocketId,
    inputNodeId,
    inputSocketId,
    fieldType,
    fieldLabel,
  } = args;

  const outputNode = flow.nodes.find((entry) => entry.id === outputNodeId);
  const inputNode = flow.nodes.find((entry) => entry.id === inputNodeId);
  if (!outputNode || !inputNode) return flow;

  const existingOutputFieldId =
    outputNode.outputs.find((binding) => binding.socketId === outputSocketId)?.fieldId ?? null;
  const existingInputFieldId =
    inputNode.inputs.find((binding) => binding.socketId === inputSocketId)?.fieldId ?? null;

  if (
    existingInputFieldId &&
    existingOutputFieldId &&
    existingInputFieldId !== existingOutputFieldId
  ) {
    return flow;
  }

  if (existingInputFieldId && !existingOutputFieldId) {
    const inputFieldHasProducer = flow.nodes.some(
      (node) =>
        node.id !== outputNodeId &&
        node.outputs.some((binding) => binding.fieldId === existingInputFieldId)
    );
    if (inputFieldHasProducer) {
      return flow;
    }
  }

  const resolvedFieldId = existingOutputFieldId ?? existingInputFieldId;

  let fields = [...flow.fields];
  let fieldId = resolvedFieldId;

  if (!fieldId) {
    const nextField: FlowFieldDefinition = {
      id: createFlowEntityId('flow-field'),
      label:
        typeof fieldLabel === 'string' && fieldLabel.trim().length > 0
          ? fieldLabel.trim()
          : 'Shared Value',
      type: fieldType,
      multiple: fieldType.startsWith('list.'),
    };
    fields = upsertFlowFields(fields, [nextField]);
    fieldId = nextField.id;
  }

  const nodes = flow.nodes.map((node) => {
    if (node.id === outputNodeId) {
      const nextOutputs = node.outputs.filter((binding) => binding.socketId !== outputSocketId);
      nextOutputs.push({ socketId: outputSocketId, fieldId: fieldId! });
      return { ...node, outputs: nextOutputs };
    }
    if (node.id === inputNodeId) {
      const nextInputs = node.inputs.filter((binding) => binding.socketId !== inputSocketId);
      nextInputs.push({ socketId: inputSocketId, fieldId: fieldId! });
      return { ...node, inputs: nextInputs };
    }
    return node;
  });

  const nextFlow: FlowDefinition = {
    ...flow,
    fields,
    nodes,
  };

  return {
    ...nextFlow,
    connections: rebuildFlowConnections(nextFlow),
  };
}

function normalizeFlowStateEntry(value: unknown): PageFlowStateEntry {
  if (!isRecord(value)) {
    return {
      values: {},
      updatedAt: FLOW_UPDATED_AT_FALLBACK,
    };
  }

  return {
    values: isRecord(value.values) ? { ...value.values } : {},
    updatedAt:
      typeof value.updatedAt === 'string' && value.updatedAt.trim().length > 0
        ? value.updatedAt
        : FLOW_UPDATED_AT_FALLBACK,
  };
}

export function normalizePageFlowState(value: unknown, flows: FlowDefinition[]): PageFlowState {
  const raw = isRecord(value) ? value : {};
  const next: PageFlowState = {};

  for (const flow of flows) {
    next[flow.id] = normalizeFlowStateEntry(raw[flow.id]);
  }

  return next;
}

export function reconcilePageFlowState(
  current: PageFlowState,
  flows: FlowDefinition[],
  fallback?: PageFlowState
): PageFlowState {
  const next: PageFlowState = {};

  for (const flow of flows) {
    next[flow.id] = current[flow.id] ?? fallback?.[flow.id] ?? {
      values: {},
      updatedAt: FLOW_UPDATED_AT_FALLBACK,
    };
  }

  return next;
}

export function setFlowFieldValue(
  state: PageFlowState,
  flowId: string,
  fieldId: string,
  value: unknown,
  updatedAt = new Date().toISOString()
): PageFlowState {
  const currentEntry = state[flowId] ?? {
    values: {},
    updatedAt: FLOW_UPDATED_AT_FALLBACK,
  };
  if (flowValuesEqual(currentEntry.values[fieldId], value)) {
    return state;
  }
  return {
    ...state,
    [flowId]: {
      values: {
        ...currentEntry.values,
        [fieldId]: value,
      },
      updatedAt,
    },
  };
}

export function patchFlowValues(
  state: PageFlowState,
  flowId: string,
  values: Record<string, unknown>,
  updatedAt = new Date().toISOString()
): PageFlowState {
  const currentEntry = state[flowId] ?? {
    values: {},
    updatedAt: FLOW_UPDATED_AT_FALLBACK,
  };
  const nextEntries = Object.entries(values);
  if (
    nextEntries.length === 0 ||
    nextEntries.every(([fieldId, value]) => flowValuesEqual(currentEntry.values[fieldId], value))
  ) {
    return state;
  }
  return {
    ...state,
    [flowId]: {
      values: {
        ...currentEntry.values,
        ...values,
      },
      updatedAt,
    },
  };
}

export function clearFlowFields(
  state: PageFlowState,
  flowId: string,
  fieldIds: string[],
  updatedAt = new Date().toISOString()
): PageFlowState {
  const currentEntry = state[flowId] ?? {
    values: {},
    updatedAt: FLOW_UPDATED_AT_FALLBACK,
  };
  const nextValues = { ...currentEntry.values };
  fieldIds.forEach((fieldId) => {
    delete nextValues[fieldId];
  });
  return {
    ...state,
    [flowId]: {
      values: nextValues,
      updatedAt,
    },
  };
}

export function findVaseFlows(flows: FlowDefinition[]): FlowDefinition[] {
  const presetMatches = flows.filter(
    (flow) => flow.scope === 'page' && flow.presetId === VASE_CUSTOMIZER_FLOW_PRESET_ID
  );
  if (presetMatches.length > 0) return presetMatches;

  const fieldMatches = flows.filter(
    (flow) =>
      flow.scope === 'page' &&
      flow.fields.some(
        (field) =>
          field.id === VASE_FLOW_FIELD_IDS.chosenProduct &&
          field.type === 'entity.product'
      )
  );
  return fieldMatches;
}

export function findPrimaryVaseFlow(flows: FlowDefinition[]): FlowDefinition | null {
  return findVaseFlows(flows)[0] ?? null;
}

export function buildVaseSelectionFlowPatch(vase: SelectedVase): Record<string, unknown> {
  return {
    [VASE_FLOW_FIELD_IDS.chosenProduct]: vase,
    [VASE_FLOW_FIELD_IDS.chosenFinish]: undefined,
    [VASE_FLOW_FIELD_IDS.chosenColours]: undefined,
    [VASE_FLOW_FIELD_IDS.chosenCombination]: undefined,
  };
}
